import Link from "next/link";
import { ShieldCheck, Calendar } from "lucide-react";
import { SITE_AUTHOR, LAST_REVIEWED } from "@/lib/constants/eeat";

export interface EeatTrustBadgeProps {
  /** Label text for the trust badge (e.g. "Research-Backed Tools", "Government-Sourced Data"). Defaults to "Research-Backed Tools". */
  label?: string;
  /** Prefix text before author name (e.g. "Built by", "Researched & maintained by"). Defaults to "Built by". */
  authorPrefix?: string;
  /** Author name override. Defaults to SITE_AUTHOR.name. */
  authorName?: string;
  /** Author credentials override. Defaults to SITE_AUTHOR.credentials. */
  authorCredentials?: string;
  /** Custom date/last updated text. Defaults to the site-wide LAST_REVIEWED constant. */
  lastUpdated?: string;
  /** Full meta line override if needed. Defaults to standard primary government sources statement. */
  metaText?: string;
  /** Additional inline style overrides for container spacing. */
  style?: React.CSSProperties;
  /** Extra CSS class names. */
  className?: string;
}

/**
 * Site-wide E-E-A-T Trust Badge component.
 * Displays human author expertise attribution, government data verification label, and review metadata.
 */
export default function EeatTrustBadge({
  label = "Research-Backed Tools",
  authorPrefix = "Built by",
  authorName = SITE_AUTHOR.name,
  authorCredentials = SITE_AUTHOR.credentials,
  lastUpdated,
  metaText,
  style,
  className = "",
}: EeatTrustBadgeProps) {
  const displayAuthorName = authorName || SITE_AUTHOR.name;
  const displayCredentials = authorCredentials !== undefined ? authorCredentials : SITE_AUTHOR.credentials;
  const defaultMeta = `Source links and illustrative assumptions appear on this page. Last reviewed ${lastUpdated || LAST_REVIEWED}.`;

  return (
    <div className={`eeat-trust-badge ${className}`.trim()} style={style}>
      <div className="eeat-badge-review">
        <ShieldCheck size={20} className="eeat-shield-icon" />
        <div className="eeat-review-text">
          <span className="eeat-review-label">{label}</span>
          <span className="eeat-review-author">
            {authorPrefix}{" "}
            <Link href="/authors/galvin-mendonca" className="author-name-link">
              {displayAuthorName}
            </Link>
            {displayCredentials ? `, ${displayCredentials}` : ""}
          </span>
        </div>
      </div>
      <div className="eeat-badge-meta">
        <Calendar size={13} />
        <span>{metaText || defaultMeta}</span>
      </div>
    </div>
  );
}
